import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function FAQ() {
  return (
    <>
      {/* Hero */}
      <section className="page-hero page-hero--short">
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Support</span>
            <h1>Frequently Asked Questions</h1>
            <p>Everything you need to know</p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">

            {/* About the Products */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="faq-section-title">About the Products</h2>

              <FAQItem
                question="What do I get when I purchase?"
                answer="You get instant access to both ebooks (Fasting Carnivore and Nano Fasting), plus all the bonus materials including quick reference guides, 30-day trackers, food lists, fasting protocols, and more. You also get access to our private community."
              />

              <FAQItem
                question="How do I access my purchase?"
                answer="Immediately after purchase, you'll receive access to download all materials. You can read them on any device—computer, tablet, or phone."
              />

              <FAQItem
                question="Is there a refund policy?"
                answer="All purchases are final. Due to the digital nature of our products, there are no refunds, returns, or exchanges. Please review our Disclaimer page for full details before purchasing."
              />

              <FAQItem
                question="Can I share my purchase with others?"
                answer="No, purchases are for individual use only. Sharing, redistributing, or reselling our products is a violation of our terms of service and copyright."
              />
            </motion.div>

            {/* About the Approach */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="faq-section-spacer"
            >
              <h2 className="faq-section-title">About the Carnivore Approach</h2>

              <FAQItem
                question="What is the carnivore diet?"
                answer="The carnivore diet is an elimination diet that consists primarily of animal products—meat, fish, eggs, and sometimes dairy. It eliminates all plant foods including vegetables, fruits, grains, legumes, nuts, and seeds."
              />

              <FAQItem
                question="Is this safe?"
                answer="We're not medical professionals and cannot provide medical advice. The information in our products is for educational purposes only. You should consult with your healthcare provider before making any dietary changes, especially if you have pre-existing conditions or take medications."
              />

              <FAQItem
                question="Will I get all the nutrients I need?"
                answer="Meat is nutritionally dense and contains all essential amino acids, many vitamins, and minerals. However, individual needs vary. We recommend working with a healthcare provider and getting appropriate testing. This is discussed in detail in the books."
              />

              <FAQItem
                question="What about fiber?"
                answer="Many people on carnivore find their digestion works well without fiber. Everyone's experience is different. The adjustment period section in our books covers what to expect during the transition."
              />

              <FAQItem
                question="Won't all that meat cause heart disease?"
                answer="The relationship between saturated fat, cholesterol, and heart disease is more nuanced than conventional wisdom suggests. We discuss the science in our materials, but ultimately recommend working with your doctor and monitoring your own health markers."
              />
            </motion.div>

            {/* About Fasting */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="faq-section-spacer"
            >
              <h2 className="faq-section-title">About Fasting</h2>

              <FAQItem
                question="What is Nano Fasting?"
                answer="Nano Fasting is our gradual approach to extending your fasting window. Instead of jumping into long fasts, you make small, sustainable increases that your body adapts to naturally. The full method is explained in the Nano Fasting book."
              />

              <FAQItem
                question="Do I have to fast to do carnivore?"
                answer="No. Carnivore and fasting are separate practices that complement each other well, but you can do either one independently. Many people start with carnivore and naturally find themselves eating less frequently."
              />

              <FAQItem
                question="Who should NOT fast?"
                answer="Fasting is not appropriate for everyone. Pregnant or breastfeeding women, those with a history of eating disorders, people with certain medical conditions, and those on certain medications should not fast without medical supervision. Always consult your healthcare provider."
              />
            </motion.div>

            {/* Getting Started */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="faq-section-spacer"
            >
              <h2 className="faq-section-title">Getting Started</h2>

              <FAQItem
                question="Is this expensive? Isn't meat costly?"
                answer="It can be done on various budgets. Ground beef, eggs, and less popular cuts are economical options. Many people find they spend less overall because they stop buying processed foods, snacks, and eating out as much."
              />

              <FAQItem
                question="What should I eat?"
                answer="The simplest starting point: beef, salt, and water. From there, you can add other meats, eggs, and potentially dairy based on your goals and how you feel. Our food lists and guides provide detailed recommendations."
              />

              <FAQItem
                question="Will I feel bad at first?"
                answer="Many people experience an adjustment period in the first 1-2 weeks as their body transitions from burning sugar to burning fat. This can include fatigue, headaches, and cravings. Our materials cover how to minimize discomfort during this phase."
              />

              <FAQItem
                question="How long before I see results?"
                answer="This varies significantly by individual. Some people notice changes within days, while others take weeks. Factors include your starting point, how strictly you follow the approach, and your individual metabolism. The stories in our books show a range of timelines."
              />
            </motion.div>

            {/* Community */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="faq-section-spacer"
            >
              <h2 className="faq-section-title">Community</h2>

              <FAQItem
                question="What is the community?"
                answer="Our private Discord community is where members share experiences, ask questions, celebrate wins, and support each other. It's included with your purchase at no additional cost."
              />

              <FAQItem
                question="How do I join the community?"
                answer="After purchase, you'll receive an invitation link to join the private Discord server. Simply click the link and follow the instructions to join."
              />
            </motion.div>

            {/* Still have questions */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="faq-contact-box"
            >
              <h3 className="faq-contact-title">Still have questions?</h3>
              <p className="faq-contact-text">
                Contact us at <a href="mailto:support@fastingcarnivore.com">support@fastingcarnivore.com</a>
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta py-responsive">
        <div className="container text-center">
          <h2 className="faq-cta-heading">Ready to Begin?</h2>
          <p className="faq-cta-body">
            Get both books, all the bonuses, and community access.
          </p>
          <Link to="/join" className="btn btn-secondary btn-lg">
            Get Started Now
          </Link>
        </div>
      </section>
    </>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="faq-item-button"
      >
        <span>{question}</span>
        <span className="faq-item-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="faq-item-answer">
          {answer}
        </div>
      )}
    </div>
  );
}
